import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LuMonitor, LuMoon, LuSun } from 'react-icons/lu';
import { useAuth } from '../AuthContext';
import { useLanguage } from '../i18n/LanguageContext';
import { apiJson } from '../apiClient';
import {
  THEME_OPTIONS,
  applyTheme,
  initThemeListener,
  readStoredTheme,
  resolveTheme,
} from '../utils/appTheme';
import './ThemeToggle.css';

const OPTION_ICONS = {
  light: LuSun,
  dark: LuMoon,
  system: LuMonitor,
};

export default function ThemeToggle({ variant = 'topbar' }) {
  const { user, refreshMe } = useAuth();
  const { t } = useLanguage();
  const userId = user?.id || 'guest';
  const [theme, setTheme] = useState(() => user?.preferences?.theme || readStoredTheme(userId));
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const fromUser = user?.preferences?.theme;
    if (fromUser) setTheme(fromUser);
    else setTheme(readStoredTheme(userId));
  }, [user?.preferences?.theme, userId]);

  useEffect(() => {
    applyTheme(theme, userId);
    return initThemeListener(userId);
  }, [theme, userId]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const select = useCallback(
    async (value) => {
      setTheme(value);
      applyTheme(value, userId);
      setOpen(false);
      if (!user?.id) return;
      try {
        await apiJson('/me/preferences', {
          method: 'PATCH',
          body: JSON.stringify({ theme: value }),
        });
        await refreshMe();
      } catch {
        /* local theme still applied */
      }
    },
    [user?.id, userId, refreshMe]
  );

  const resolved = resolveTheme(theme);
  const ActiveIcon = resolved === 'dark' ? LuMoon : LuSun;

  return (
    <div className={`theme-toggle theme-toggle--${variant}`} ref={wrapRef}>
      <button
        type="button"
        className="theme-toggle__btn"
        aria-label={t('theme.toggle')}
        aria-expanded={open}
        aria-haspopup="true"
        title={t('theme.toggle')}
        onClick={() => setOpen((o) => !o)}
      >
        <ActiveIcon className="theme-toggle__btn-icon" aria-hidden size={18} strokeWidth={2.25} />
      </button>
      {open ? (
        <div className="theme-toggle__panel" role="menu" aria-label={t('theme.appearance')}>
          <div className="theme-toggle__panel-head">{t('theme.appearance')}</div>
          {THEME_OPTIONS.map((opt) => {
            const Icon = OPTION_ICONS[opt.value] || LuSun;
            const active = theme === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                className={`theme-toggle__option${active ? ' is-active' : ''}`}
                onClick={() => void select(opt.value)}
              >
                <span className="theme-toggle__option-icon" aria-hidden>
                  <Icon size={16} strokeWidth={2.25} />
                </span>
                <span className="theme-toggle__option-label">{t(`theme.${opt.value}`)}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
